import type { Metadata, MetadataPaymentInstrument } from '../types/metadata';
import { PaymentMethodType, PaymentMode } from '../types/paymentMethodSet';
import { Vendor } from '../types/paymentMethodSet';
import type { ThemeMode } from '../types/theme';

export class MetadataReader {
  private metadata: Metadata;

  constructor(metadata: Metadata) {
    this.metadata = metadata;
  }

  private getPaymentInstrumentType(type: string): MetadataPaymentInstrument {
    return Object.values(this.metadata.paymentInstrumentTypes).find(
      (paymentInstrumentType) => paymentInstrumentType.apiValue === type
    );
  }

  private getPaymentInstrumentTypeName(type: PaymentMethodType): string {
    for (const [key, value] of Object.entries(this.metadata.paymentInstrumentTypes)) {
      if (value.apiValue === type?.toString()) {
        return key;
      }
    }
  }

  private getGatewayPaymentInstrument(vendor: Vendor, type: string): Record<string, any> {
    const gateway = this.metadata.gateways[vendor];
    return gateway?.paymentInstruments[type] ?? {};
  }

  /**
   * Returns true if the given payment method type indicates that payment can be saved.
   * @param type payment method type
   * @returns if the payment method type can be saved
   */
  canSavePayment(type: PaymentMethodType): boolean {
    const paymentInstrumentType: MetadataPaymentInstrument = this.getPaymentInstrumentType(type);
    return paymentInstrumentType?.canSavePayment ?? false;
  }

  /**
   * Returns true if the given payment method type supports manual capture, or false if it only
   * supports automatic capture.
   * @param type payment method type
   * @returns if the payment method type supports manual capture
   */
  supportsManualCapture(type: string): boolean {
    const paymentInstrumentType: MetadataPaymentInstrument = this.getPaymentInstrumentType(type);
    return paymentInstrumentType?.supportsManualCapture ?? false;
  }

  /**
   * Returns true if the given currency is applicable for the given payment method type.
   * @param type payment method type
   * @param currency currency code
   * @returns true if applicable
   */
  currencyIsApplicable(type: PaymentMethodType, currency: string): boolean {
    const paymentInstrumentType: MetadataPaymentInstrument = this.getPaymentInstrumentType(type);
    if (!paymentInstrumentType) {
      // Exclude payment method types with no corresponding payment instrument
      return false;
    }

    if (!currency) {
      // When no currency is provided, all payment instrument types are to be included
      return true;
    }

    if (paymentInstrumentType.applicableCurrencies[0] === '*') {
      // All currencies apply for payment instrument type
      return true;
    }

    // Check applicable currencies for payment instrument type
    return paymentInstrumentType.applicableCurrencies.includes(currency.toUpperCase());
  }

  /**
   * Returns an object containing the additional details to include in a payment method event
   * when the given payment method type is selected.
   * @param type payment method type
   * @returns object containing additional event details
   */
  getAdditionalPaymentMethodEventDetails(type: PaymentMethodType): Record<string, any> {
    const paymentInstrumentType: MetadataPaymentInstrument = this.getPaymentInstrumentType(type);
    return {
      requiresPayButton: paymentInstrumentType?.requiresPayButton,
      requiresBillingDetails: paymentInstrumentType?.requiresBillingDetails,
      requiresEmail: paymentInstrumentType?.requiresEmail,
      requiresPhone: paymentInstrumentType?.requiresPhone,
    };
  }

  /**
   * Returns the default payer-facing name for the given payment method type.
   * @param type payment method type
   * @returns default payer facing name
   */
  getName(type: PaymentMethodType): string {
    return this.getPaymentInstrumentType(type)?.name ?? '';
  }

  /**
   * Returns the vendor specific payment method mapping for the given payment method type.
   * @param type payment method type
   * @param vendor vendor
   * @returns vendor specific payment method name
   */
  getGatewayPaymentMethodMapping(type: PaymentMethodType, vendor: Vendor, currency?: string): string {
    const name = this.getPaymentInstrumentTypeName(type);
    const gatewayPaymentMethodMappings = this.metadata.gateways[vendor]?.paymentMethodMappings ?? {};
    for (const [key, value] of Object.entries(gatewayPaymentMethodMappings)) {
      if (
        value.paymentMethodType === name &&
        (currency === undefined ||
          value.applicableCurrencies === undefined ||
          value.applicableCurrencies.includes(currency.toUpperCase()))
      ) {
        return key;
      }
    }
    return '';
  }
  /**
   * Returns the URL of the icon to use to represent the given payment method type
   * in the given theme mode.
   * @param type payment method type
   * @param mode theme mode
   * @param variant optional variant to filter by
   * @returns URL of the payment method icon
   */
  getPaymentMethodIconUrl(type: PaymentMethodType, mode: ThemeMode, variant?: string): string {
    const paymentInstrumentTypeName = this.getPaymentInstrumentTypeName(type);
    const paymentMethodType = this.metadata.paymentMethodTypes[paymentInstrumentTypeName];
    if (!paymentMethodType) {
      // No icon for types with no corresponding payment method type
      return '';
    }

    const modeName = mode?.toString().toLowerCase();

    // If variant is provided, look for images with matching variant
    if (variant) {
      const modeIcon = paymentMethodType.images.find((image) => image.mode === modeName && image.variant === variant);
      if (modeIcon) {
        // Use payment method icon image specific to theme mode and variant
        return modeIcon.src;
      }

      // Use payment method default icon image with variant
      const defaultIcon = paymentMethodType.images.find((image) => !image.mode && image.variant === variant);
      if (defaultIcon) {
        return defaultIcon.src;
      }
    }

    // If no variant or no matching variant found, fall back to images without variant
    const modeIcon = paymentMethodType.images.find((image) => image.mode === modeName && !image.variant);
    if (modeIcon) {
      // Use payment method icon image specific to theme mode
      return modeIcon.src;
    }

    // Use payment method default icon image without variant
    const defaultIcon = paymentMethodType.images.find((image) => !image.mode && !image.variant);
    if (defaultIcon) {
      return defaultIcon.src;
    }

    // If no images found at all, return empty string
    return '';
  }

  /**
   * Returns the URL of the icon to use to represent the given variant of the given canonical payment method,
   * in the given theme mode.
   * @param vendor vendor
   * @param variant payment method variant
   * @param canonicalPaymentMethodType canonical payment method type name, such as <code>"Card"</code>
   * @param mode theme mode
   * @returns URL of the card brand icon
   */
  getVariantPaymentMethodIconUrl(
    vendor: Vendor,
    variant: string,
    canonicalPaymentMethodType: string,
    mode: ThemeMode
  ): string {
    const gatewayPaymentMethodMappings = this.metadata.gateways[vendor]?.paymentMethodMappings ?? {};

    // Find the gateway payment method mapping for canonical payment method type
    const cardPaymentMethodMapping = Object.values(gatewayPaymentMethodMappings).find(
      (paymentMethodMapping) => paymentMethodMapping.paymentMethodType === canonicalPaymentMethodType
    );

    // Get the canonical payment method type mapped to the brand as a variant
    const paymentMethodType =
      this.metadata.paymentMethodTypes[cardPaymentMethodMapping?.variantPaymentMethodTypes[variant]];
    if (!paymentMethodType) {
      // Use credit card icon for brands with no corresponding payment method type
      return this.getPaymentMethodIconUrl(PaymentMethodType.CARD, mode);
    }

    const modeName = mode?.toString().toLowerCase();
    const modeIcon = paymentMethodType.images.find((image) => image.mode === modeName);
    if (modeIcon) {
      // Use brand icon image specific to theme mode
      return modeIcon.src;
    }

    // Use brand default icon image
    return paymentMethodType.images.find((image) => !image.mode).src;
  }

  /**
   * Returns the payment mode metadata in the gateway-specific payment instrument information
   * for the given vendor and payment method type, or an empty object if metadata are not present.
   * @param vendor vendor
   * @param type payment method type
   * @param mode payment mode
   * @returns payment mode metadata
   */
  getGatewayPaymentInstrumentMode(vendor: Vendor, type: string, mode: PaymentMode): Record<string, any> {
    const gatewayPaymentInstrument = this.getGatewayPaymentInstrument(vendor, type);
    return gatewayPaymentInstrument.modes?.[mode] ?? {};
  }

  /**
   * Returns an array containing all of the Stripe payment method names for all of the payment method types
   * which support payment mode {@link PaymentMode.EXPRESS}.
   * @returns array of Stripe payment method names
   */
  getStripeExpressPaymentMethods(): string[] {
    const stripe = this.metadata.gateways[Vendor.STRIPE];
    const paymentMethods: string[] = [];
    Object.values(stripe.paymentInstruments).forEach((paymentInstrument) => {
      const paymentMethod: string = paymentInstrument.modes[PaymentMode.EXPRESS]?.paymentMethod;
      if (paymentMethod) {
        paymentMethods.push(paymentMethod);
      }
    });
    return paymentMethods;
  }
}
